// Pure helpers for the chat harness's memory of the analytics API.
//
// The chat learns the edges of the analytics API the way a person does:
// a query fails, a later query on the same entity succeeds, and the
// difference between the two is the lesson. Everything here works on
// SHAPES, never values: an entity name, the field and edge enums a spec
// references, the predicate operators, and the kind of time window.
// Predicate values, result rows, and free text never enter a shape, so
// a lesson can be persisted and shown in a prompt without carrying the
// data the query was about.

const FIELD_PREFIX = "DIMENSION_FIELD_";
const ENTITY_PREFIX = "ENTITY_TYPE_";
const EDGE_PREFIX = "EDGE_TYPE_";
const PRED_OPS = new Set([
    "EQ", "NE", "IN", "NOT_IN", "LT", "LTE", "GT", "GTE", "LIKE", "ILIKE", "NOT_LIKE",
    "IS_NULL", "IS_NOT_NULL", "CONTAINS", "BETWEEN",
]);

function walk(node, visit, depth = 0) {
    if (depth > 24 || node == null) return;
    if (Array.isArray(node)) {
        for (const item of node) walk(item, visit, depth + 1);
        return;
    }
    if (typeof node !== "object") return;
    visit(node);
    for (const value of Object.values(node)) {
        if (value && typeof value === "object") walk(value, visit, depth + 1);
    }
}

function sortedUnique(values) {
    return Array.from(new Set(values.filter(Boolean))).sort();
}

function timeShapeOf(timeRange) {
    if (!timeRange || typeof timeRange !== "object") return "none";
    if (timeRange.absolute) {
        const keys = Object.keys(timeRange.absolute).sort();
        return `absolute(${keys.join(",")})`;
    }
    if (timeRange.relative) {
        const keys = Object.keys(timeRange.relative).sort();
        return `relative(${keys.join(",")})`;
    }
    return `other(${Object.keys(timeRange).sort().join(",")})`;
}

// Structural summary of an analytics Query spec. Accepts the spec as
// an object or as the JSON string some model turns emit.
export function specShape(spec) {
    let parsed = spec;
    if (typeof parsed === "string") {
        try { parsed = JSON.parse(parsed); } catch { parsed = null; }
    }
    if (!parsed || typeof parsed !== "object") {
        return { entity: null, edges: [], fields: [], ops: [], keys: [], timeShape: "none", limit: null, semantic: false, malformed: true };
    }
    const fields = [];
    const edges = [];
    const ops = [];
    const entities = [];
    const keys = [];
    walk(parsed, (node) => {
        for (const [key, value] of Object.entries(node)) {
            if (key !== "value") keys.push(key);
            if (typeof value !== "string") continue;
            if (value.startsWith(FIELD_PREFIX)) fields.push(value);
            else if (value.startsWith(ENTITY_PREFIX)) entities.push(value);
            else if (value.startsWith(EDGE_PREFIX)) edges.push(value);
            else if (key === "op" && PRED_OPS.has(value)) ops.push(value);
        }
    });
    const rootEntity = typeof parsed.root?.entity === "string" ? parsed.root.entity : entities[0] || null;
    const limit = Number.isFinite(Number(parsed.limit)) && parsed.limit != null ? Number(parsed.limit) : null;
    return {
        entity: rootEntity,
        edges: sortedUnique(edges),
        fields: sortedUnique(fields),
        ops: sortedUnique(ops),
        keys: sortedUnique(keys),
        timeShape: timeShapeOf(parsed.timeRange),
        limit,
        semantic: Boolean(parsed.semanticSearch),
        malformed: false,
    };
}

// Coarse class of an analytics error. Mirrors the eval bank's classes
// so a lesson's class and a scored run's error class line up.
export function classifyQueryError(text) {
    const raw = String(text || "");
    const t = extractMessage(raw).toLowerCase();
    if (t.includes("proto:")) {
        if (t.includes("invalid value for enum")) {
            if (t.includes("dimension_field_")) return "unknown_field";
            if (t.includes("entity_type_") || t.includes("edge_type_")) return "unknown_schema_name";
            return "unknown_enum";
        }
        return "proto_syntax";
    }
    if (t.includes("unknown field")) return "unknown_field";
    if (t.includes("unknown entity") || t.includes("unknown edge")) return "unknown_schema_name";
    if (t.includes("exceeds the") && t.includes("row bound")) return "row_bound";
    if (t.includes("exceeded an execution limit")) return "scan_ceiling";
    if (t.includes("requires a conjunctive like or ilike")) return "text_search_needs_term";
    if (t.includes("trace_id") && (t.includes("requires") || t.includes("must"))) return "trace_id_required";
    if (t.includes("time range") || t.includes("timerange") || t.includes("time_range") || t.includes("window")) return "time_range";
    if (t.includes("syntax error") || t.includes("unknown field name")) return "proto_syntax";
    if (t.includes("validation:")) return "validation";
    const low = raw.toLowerCase();
    if (low.includes(" 400")) return "http_400";
    if (low.includes(" 403")) return "http_403";
    if (/ 5\d\d/.test(low)) return "http_5xx";
    return "other";
}

// The gateway wraps planner errors as `ExecuteQuery 400: {"code":3,"message":"..."}`.
// The message is the part worth remembering.
export function extractMessage(text) {
    const raw = String(text || "");
    const m = raw.match(/"message"\s*:\s*"((?:[^"\\]|\\.)*)"/);
    if (!m) return raw;
    try { return JSON.parse(`"${m[1]}"`); } catch { return m[1]; }
}

// Quoted tokens that name a schema enum or a JSON key are the point of
// the message; quoted values (hostnames, search terms) are not.
const KEEP_QUOTED = /^(?:[A-Z][A-Z0-9_]{2,}|[a-z][A-Za-z0-9_]{0,40})$/;
function replaceQuoted(text, quote) {
    const re = quote === '"' ? /"([^"\n]*)"/g : /'([^'\n]*)'/g;
    return text.replace(re, (whole, inner) => (KEEP_QUOTED.test(inner) ? inner : "<str>"));
}

// The error message with everything instance-specific replaced, so two
// occurrences of the same rule violation produce the same template.
export function errorTemplate(text) {
    let s = extractMessage(text)
        .replace(/\s*\(line \d+:\d+\):?/g, "")
        .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, "<uuid>")
        .replace(/\d{4}-\d{2}-\d{2}T[\d:.]+Z?/g, "<time>");
    s = replaceQuoted(s, '"');
    s = replaceQuoted(s, "'");
    return s
        .replace(/\b\d+(\.\d+)?\s*(gib|mib|kib|gb|mb|kb|bytes|rows|ms|s)\b/gi, "<qty>")
        .replace(/\b\d{3,}\b/g, "<n>")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 240);
}

export function errorSignature({ toolName = "analytics_query", errorText, shape }) {
    const cls = classifyQueryError(errorText);
    const template = errorTemplate(errorText);
    const entity = shape?.entity || null;
    return {
        toolName,
        class: cls,
        template,
        entity,
        key: `${toolName}|${cls}|${entity || "*"}|${template.slice(0, 160)}`,
    };
}

function listDiff(before, after) {
    const b = new Set(before || []);
    const a = new Set(after || []);
    return {
        removed: Array.from(b).filter((x) => !a.has(x)),
        added: Array.from(a).filter((x) => !b.has(x)),
    };
}

// What changed between a failing shape and the shape that later worked.
export function shapeDiff(before, after) {
    const out = [];
    if (!before || !after) return out;
    if (before.entity !== after.entity) out.push({ what: "entity", removed: [before.entity].filter(Boolean), added: [after.entity].filter(Boolean) });
    for (const what of ["fields", "edges", "ops", "keys"]) {
        const d = listDiff(before[what], after[what]);
        if (d.removed.length || d.added.length) out.push({ what, ...d });
    }
    if (before.timeShape !== after.timeShape) out.push({ what: "timeShape", removed: [before.timeShape], added: [after.timeShape] });
    if (before.limit !== after.limit) {
        out.push({ what: "limit", removed: before.limit == null ? [] : [String(before.limit)], added: after.limit == null ? [] : [String(after.limit)] });
    }
    if (before.semantic !== after.semantic) out.push({ what: "semantic", removed: before.semantic ? ["semanticSearch"] : [], added: after.semantic ? ["semanticSearch"] : [] });
    return out;
}

function shortName(name) {
    return String(name).replace(FIELD_PREFIX, "").replace(ENTITY_PREFIX, "").replace(EDGE_PREFIX, "");
}

// One line a model can read: "dropped fields A, B; added op ILIKE".
export function describeDiff(diff, { maxItems = 4 } = {}) {
    if (!diff?.length) return "";
    const parts = [];
    for (const d of diff) {
        const removed = d.removed.slice(0, maxItems).map(shortName).join(", ");
        const added = d.added.slice(0, maxItems).map(shortName).join(", ");
        if (d.what === "timeShape" || d.what === "entity") {
            parts.push(`${d.what} ${removed || "none"} -> ${added || "none"}`);
            continue;
        }
        const bits = [];
        if (removed) bits.push(`dropped ${removed}${d.removed.length > maxItems ? ", ..." : ""}`);
        if (added) bits.push(`added ${added}${d.added.length > maxItems ? ", ..." : ""}`);
        parts.push(`${d.what}: ${bits.join("; ")}`);
    }
    return parts.join(" | ");
}

function looksLikeRetry(before, after) {
    const a = shortName(before?.entity || "");
    const b = shortName(after?.entity || "");
    if (a && b && (a.includes(b) || b.includes(a))) return true;
    const fields = new Set(before?.fields || []);
    return (after?.fields || []).some((f) => fields.has(f));
}

// Pair each failed call with the first later successful call that looks
// like a retry of it: same root entity, or (when the fix was to change
// entity) the very next successful call after the error. `events` are
// ledger records in call order: { kind: "error"|"ok", shape, signature,
// toolCallId, index }.
export function pairFixes(events, { maxGap = 6 } = {}) {
    const pairs = [];
    const unpaired = [];
    for (let i = 0; i < events.length; i++) {
        const ev = events[i];
        if (ev.kind !== "error") continue;
        let fix = null;
        for (let j = i + 1; j < events.length && j <= i + maxGap; j++) {
            const later = events[j];
            if (later.kind !== "ok") continue;
            if (later.shape?.entity && later.shape.entity === ev.shape?.entity) { fix = later; break; }
        }
        if (!fix) {
            // The fix may have been to change entity (PROMPT -> PROMPT_SEARCH).
            // Only trust that reading when the next success still looks
            // like the same question: a related entity name or shared fields.
            const next = events.slice(i + 1, i + 3).find((e) => e.kind === "ok");
            if (next && ev.shape?.entity && next.shape?.entity !== ev.shape.entity && looksLikeRetry(ev.shape, next.shape)) fix = next;
        }
        if (fix) pairs.push({ error: ev, fix, diff: shapeDiff(ev.shape, fix.shape) });
        else unpaired.push(ev);
    }
    return { pairs, unpaired };
}

// Rank stored entries against a fresh failure. Exact signature key beats
// same class on the same entity, which beats same class anywhere. An
// entry with no entity scope applies to every entity.
export function matchEntries(entries, { signature, shape }) {
    const out = [];
    for (const entry of entries || []) {
        if (entry.status && entry.status !== "active") continue;
        if (entry.scope?.tool && signature?.toolName && entry.scope.tool !== signature.toolName) continue;
        let score = 0;
        if (entry.signatureKey && signature?.key && entry.signatureKey === signature.key) score = 3;
        else if (entry.scope?.errorClass && signature?.class && entry.scope.errorClass === signature.class) {
            const entityMatch = !entry.scope.entity || entry.scope.entity === (shape?.entity || signature?.entity);
            score = entityMatch ? 2 : 0;
        } else if (!entry.scope?.errorClass && entry.scope?.entity && entry.scope.entity === (shape?.entity || signature?.entity)) {
            const fieldOverlap = (entry.scope.fields || []).some((f) => (shape?.fields || []).includes(f));
            score = fieldOverlap ? 1 : 0;
        }
        if (score > 0) out.push({ entry, score });
    }
    out.sort((a, b) => b.score - a.score || (b.entry.confidence || 0) - (a.entry.confidence || 0));
    return out.map((m) => m.entry);
}

// Stable hash of the schema's entity, field, and edge names. A lesson is
// stamped with the hash of the schema it was learned against; when the
// schema changes, lessons whose names no longer exist go stale.
export function schemaNames(schema) {
    const entities = new Set();
    const fields = new Set();
    const edges = new Set();
    for (const e of schema?.entities || []) {
        const name = e.entity || e.name;
        if (name) entities.add(name);
        for (const f of e.fields || []) {
            const fname = f.field || f.name;
            if (fname) fields.add(fname);
        }
    }
    for (const ed of schema?.edges || []) if (ed.edge) edges.add(ed.edge);
    return { entities, fields, edges };
}

export function schemaHash(schema) {
    const names = schemaNames(schema);
    const all = [...names.entities, ...names.fields, ...names.edges].sort();
    if (!all.length) return null;
    let h = 0x811c9dc5;
    for (const s of all) {
        for (let i = 0; i < s.length; i++) {
            h ^= s.charCodeAt(i);
            h = Math.imul(h, 0x01000193) >>> 0;
        }
        h ^= 0x7c;
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h.toString(16).padStart(8, "0");
}
